"use client";

import { useState, useEffect } from "react";
import { Navbar, Footer } from "@/components/layout/NavbarFooter";
import { useRouter } from "next/navigation";
import LeadershipCarousel from "@/components/ui/LeadershipCarousel";
import ServicesTabs from "@/components/ui/ServicesTabs";
import MapSection from "@/components/ui/MapSection";
import TestimonialCarousel from "@/components/ui/TestimonialCarousel";
import Counter from "@/components/ui/Counter";
import dynamic from "next/dynamic";
import NextImage from "next/image";
import BrandsSection from "@/components/BrandsSection";

const Hero3DCanvas = dynamic(() => import("@/components/ui/Hero3DCanvas"), {
  ssr: false,
  loading: () => (
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white to-orange-50/20" />
  ),
});

const Milestones3DCanvas = dynamic(
  () => import("@/components/ui/Milestones3DCanvas"),
  {
    ssr: false,
    loading: () => (
      <div className="absolute inset-0 bg-gradient-to-t from-slate-50 to-white" />
    ),
  },
);

const CoreValues3DCanvas = dynamic(
  () => import("@/components/ui/CoreValues3DCanvas"),
  {
    ssr: false,
    loading: () => <div className="absolute inset-0 bg-[#040d21]" />,
  },
);

const Services3DCanvas = dynamic(
  () => import("@/components/ui/Services3DCanvas"),
  {
    ssr: false,
    loading: () => <div className="absolute inset-0 bg-[#040d21]" />,
  },
);

const About3DCanvas = dynamic(() => import("@/components/ui/About3DCanvas"), {
  ssr: false,
  loading: () => <div className="absolute inset-0 bg-[#040d21]" />,
});

const News3DCanvas = dynamic(() => import("@/components/ui/News3DCanvas"), {
  ssr: false,
  loading: () => <div className="absolute inset-0 bg-[#040d21]" />,
});
import {
  coreValues,
  leadership,
  services,
  stats,
  testimonials,
  milestones,
} from "@/data/homeData";
import { useLanguage } from "@/context/LanguageContext";
import {
  jobListings,
  careerValues as careerCoreValues,
} from "@/data/careersData";
import {
  Heart,
  ShieldCheck,
  Users,
  Zap,
  Cloud,
  Code,
  School,
  Video,
  TrendingUp,
  Rocket,
  Calendar,
  ChevronRight,
  ArrowRight,
  MapPin,
  Clock,
  Plus,
  Star,
  Quote,
  Phone,
  Mail,
  Link2,
} from "lucide-react";
import { toast } from "react-hot-toast";

const iconMap: Record<string, any> = {
  favorite: Heart,
  verified_user: ShieldCheck,
  groups: Users,
  bolt: Zap,
  cloud_done: Cloud,
  developer_mode: Code,
  school: School,
  movie_edit: Video,
  trending_up: TrendingUp,
  rocket_launch: Rocket,
  psychology: Users,
};

import { newsService } from "@/services/newsService";
import { serviceService } from "@/services/serviceService";
import { jobService } from "@/services/jobService";
import homepageService from "@/services/homepageService";
import { Service, News, Job } from "@/types";
import { formatExcerpt } from "@/utils/htmlUtils";
import { formatDate } from "@/utils/dateFormatter";

export default function Home() {
  const { t, language } = useLanguage();
  const router = useRouter();

  const [fetchedServices, setFetchedServices] = useState<Service[]>([]);
  const [fetchedNews, setFetchedNews] = useState<News[]>([]);
  const [fetchedJobs, setFetchedJobs] = useState<Job[]>([]);
  const [homeContent, setHomeContent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleCopyJobLink = (slug: string) => {
    if (typeof window !== "undefined") {
      const url = `${window.location.origin}/careers/${slug}`;
      navigator.clipboard.writeText(url);
      toast.success(/* t("common.copied") || */ "Đã sao chép liên kết!");
    }
  };

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const [servicesData, newsData, jobsData, dynamicContent] = await Promise.all([
          serviceService.getServices(),
          newsService.getNews(),
          jobService.getJobPostings({ limit: 4, status: "open" }),
          homepageService.getContent(language)
        ]);

        setFetchedServices(servicesData);
        setFetchedNews(newsData.docs);
        setFetchedJobs(jobsData);
        setHomeContent(dynamicContent);
      } catch (error) {
        console.error("Failed to fetch homepage data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHomeData();
  }, [language]);

  // Helper to get dynamic content with translation fallback
  const getC = (section: string, field: string, fallbackKey: string) => {
    if (homeContent && homeContent[section] && homeContent[section][field]) {
      return homeContent[section][field];
    }
    return t(fallbackKey);
  };

  return (
    <>
      <Navbar cmsData={homeContent?.navigation} />
      <main className="min-h-screen bg-white text-brand-dark font-sans selection:bg-brand-primary selection:text-white">
        {/* BEGIN: Hero Section with 3D Canvas */}
        <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-slate-900 via-[#0a192f] to-[#040d21] pt-20">
          {/* 3D Canvas Background */}
          <Hero3DCanvas />

          {/* Gradient overlay for text readability (Dark theme for cyberpunk) */}
          <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0a192f]/80 via-transparent to-transparent pointer-events-none" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full flex flex-col items-center">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-3 px-6 py-2 bg-blue-500/10 backdrop-blur-md rounded-full text-brand-primary text-sm font-bold border border-blue-500/20 mb-8 shadow-[0_0_15px_rgba(0,102,255,0.3)] mx-auto">
                <span className="flex h-2 w-2 rounded-full bg-brand-primary animate-ping"></span>
                {getC("hero", "badge", "home.hero.badge")}
              </div>
              <h1 className="text-5xl sm:text-6xl md:text-[80px] font-extrabold leading-tight mb-8 text-white tracking-tight">
                {getC("hero", "titlePart1", "home.hero.titlePart1")}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00f3ff] to-[#0066ff] drop-shadow-[0_0_10px_rgba(0,243,255,0.4)]">
                  {getC("hero", "titlePart2", "home.hero.titlePart2")}
                </span>{" "}
                {getC("hero", "titlePart3", "home.hero.titlePart3")}
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-medium max-w-2xl mx-auto mb-10">
                {getC("hero", "subtitle", "home.hero.subtitle")}
              </p>

              <div className="flex flex-wrap justify-center gap-4 mb-16">
                <a
                  className="bg-brand-primary text-white px-8 py-4 rounded-2xl font-extrabold hover:bg-blue-600 transition-all shadow-[0_0_20px_rgba(0,102,255,0.4)] hover:shadow-[0_0_30px_rgba(0,102,255,0.6)] hover:-translate-y-0.5 inline-flex items-center gap-2 cursor-pointer border border-blue-400/50"
                  href="#dich-vu"
                >
                  {getC("hero", "cta", "home.hero.cta")}
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  className="bg-[#0a192f]/50 backdrop-blur-md text-[#00f3ff] px-8 py-4 rounded-2xl font-extrabold border-2 border-[#00f3ff]/30 hover:border-[#00f3ff] hover:bg-[#00f3ff]/10 hover:shadow-[0_0_20px_rgba(0,243,255,0.3)] transition-all inline-flex items-center gap-2 cursor-pointer shadow-sm"
                  href="#lien-he"
                >
                  {getC("hero", "contactCTA", "home.hero.contactCTA")}
                </a>
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
                <div className="flex items-center gap-4 bg-[#0a192f]/60 backdrop-blur-md px-6 py-4 rounded-2xl border border-blue-500/20 shadow-[0_0_15px_rgba(0,0,0,0.5)]">
                  <div className="text-4xl sm:text-5xl font-extrabold text-white leading-none">
                    {getC("hero", "statsValue", "150")}<span className="text-[#00f3ff]">%</span>
                  </div>
                  <div className="text-sm text-slate-300 font-semibold max-w-[120px] leading-tight text-left">
                    {getC("hero", "statsLabel", "home.hero.statsLabel")}
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-[#0a192f]/60 backdrop-blur-md px-6 py-4 rounded-2xl border border-blue-500/20 shadow-[0_0_15px_rgba(0,0,0,0.5)]">
                  <div className="text-4xl sm:text-5xl font-extrabold text-white leading-none">
                    {getC("hero", "ecosystemValue", "18")}<span className="text-[#00f3ff]">+</span>
                  </div>
                  <div className="text-sm text-slate-300 font-semibold max-w-[120px] leading-tight text-left">
                    {getC("hero", "ecosystemLabel", "home.hero.ecosystemServices")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom gradient fade */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#040d21] to-transparent z-[2]" />
        </section>

        {/* BEGIN: About Us Section */}
        <section
          className="py-32 bg-[#040d21] relative overflow-hidden section-visibility"
          id="ve-chung-toi"
        >
          {/* Three.js 3D Background */}
          <About3DCanvas />

          {/* Overlay gradient for readability */}
          <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#040d21]/60 via-transparent to-[#040d21]/80 pointer-events-none" />

          {/* Neon accent glows */}
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-[#0066ff]/8 rounded-full blur-[150px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[350px] bg-[#00f3ff]/6 rounded-full blur-[120px] pointer-events-none" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Section Header */}
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#00f3ff]/10 border border-[#00f3ff]/20 text-[#00f3ff] font-black text-[10px] uppercase tracking-[0.2em] mb-6 shadow-[0_0_15px_rgba(0,243,255,0.2)]">
                <span className="w-2 h-2 rounded-full bg-[#00f3ff] animate-pulse shadow-[0_0_8px_#00f3ff]" />
                {getC("about", "badge", "home.about.badge")}
              </div>
              <h2 className="text-4xl md:text-[64px] font-black text-white leading-[1.1] tracking-tighter">
                {getC("about", "title", "home.about.title")}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00f3ff] to-[#0066ff] drop-shadow-[0_0_20px_rgba(0,243,255,0.4)]">
                  {getC("about", "titleAccent", "home.about.titleAccent")}
                </span>
              </h2>
            </div>

            {/* Two Column Layout: Content + Image */}
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              {/* Left: Company Description */}
              <div className="order-2 lg:order-1">
                <p className="text-lg text-slate-300 leading-relaxed font-medium mb-12">
                  {getC("about", "description", "home.about.description")}
                </p>

                {/* 6 Core Pillars Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    { key: "bestSolution", icon: "workspace_premium" },
                    { key: "friendlyTeam", icon: "diversity_3" },
                    { key: "integrity", icon: "verified_user" },
                    { key: "creativity", icon: "lightbulb" },
                    { key: "teamwork", icon: "groups" },
                    { key: "passion", icon: "local_fire_department" },
                  ].map((pillar) => (
                    <div
                      key={pillar.key}
                      className="group relative bg-[#0a192f]/80 backdrop-blur-md p-5 rounded-2xl border border-[#00f3ff]/10 hover:border-[#00f3ff]/50 hover:shadow-[0_0_30px_rgba(0,243,255,0.15)] transition-all duration-500 hover:-translate-y-1 cursor-default"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-[#0066ff]/20 to-[#00f3ff]/20 rounded-xl flex items-center justify-center mb-3 group-hover:from-[#0066ff] group-hover:to-[#00f3ff] transition-all duration-500 group-hover:shadow-[0_0_20px_rgba(0,243,255,0.3)]">
                        <span className="material-symbols-outlined text-xl text-[#00f3ff] group-hover:text-white transition-colors">
                          {pillar.icon}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-slate-300 group-hover:text-[#00f3ff] transition-colors leading-tight">
                        {t(`home.about.pillars.${pillar.key}`)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Image */}
              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative group">
                  {/* Neon glow behind image */}
                  <div className="absolute -inset-3 bg-gradient-to-br from-[#0066ff]/30 to-[#00f3ff]/30 rounded-[2.5rem] blur-2xl opacity-50 group-hover:opacity-80 transition-opacity duration-700 animate-pulse" />
                  <div className="relative rounded-[2rem] overflow-hidden shadow-[0_0_40px_rgba(0,243,255,0.15)] border-2 border-[#00f3ff]/20 group-hover:border-[#00f3ff]/50 group-hover:shadow-[0_0_60px_rgba(0,243,255,0.25)] transition-all duration-700">
                    <NextImage
                      src="/about_us.jpg"
                      alt="AHV Holding - Đội ngũ nhân sự và văn hóa doanh nghiệp chuyển đổi số"
                      width={600}
                      height={750}
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                      priority
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Statement Card */}
            <div className="max-w-4xl mx-auto">
              <div className="relative bg-gradient-to-br from-[#0a192f] to-[#060e1f] p-12 lg:p-16 rounded-[3rem] overflow-hidden border border-[#00f3ff]/15 group hover:border-[#00f3ff]/30 transition-all duration-700 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                {/* Decorative glows */}
                <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#00f3ff]/8 rounded-full blur-[80px] -z-0 animate-pulse" />
                <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#0066ff]/8 rounded-full blur-[60px] -z-0" />
                {/* Scanline effect */}
                <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,243,255,0.02)_50%)] bg-[length:100%_4px] pointer-events-none rounded-[3rem]" />

                <div className="relative z-10 text-center">
                  <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#00f3ff]/10 border border-[#00f3ff]/20 text-[#00f3ff] font-black text-[10px] uppercase tracking-[0.2em] mb-8 shadow-[0_0_20px_rgba(0,243,255,0.2)]">
                    <span className="material-symbols-outlined text-sm">
                      rocket_launch
                    </span>
                    {homeContent?.about?.mission?.badge || t("home.about.mission.badge")}
                  </div>
                  <p className="text-xl lg:text-2xl text-slate-200 leading-relaxed font-medium italic">
                    &ldquo;{homeContent?.about?.mission?.content || t("home.about.mission.content")}&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BEGIN: Core Values (Cyberpunk Tech Theme) */}
        <section
          className="py-32 bg-[#040d21] relative overflow-hidden section-visibility"
          id="gia-tri"
        >
          {/* 3D Canvas Background */}
          <CoreValues3DCanvas />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-12">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-[#00f3ff] font-bold text-[10px] uppercase tracking-[0.2em] mb-6 shadow-[0_0_10px_rgba(0,243,255,0.2)]">
                  <span className="w-2 h-2 rounded-full bg-[#00f3ff] animate-pulse shadow-[0_0_10px_#00f3ff]"></span>
                  {getC("values", "badge", "home.values.badge")}
                </div>
                <h2 className="text-4xl md:text-6xl font-black text-white leading-[1.1] tracking-tighter">
                  {getC("values", "title", "home.values.title").split(" ")[0]}{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00f3ff] to-[#0066ff] drop-shadow-[0_0_5px_rgba(0,243,255,0.3)]">
                    {getC("values", "title", "home.values.title").split(" ").slice(1).join(" ")}
                  </span>
                </h2>
              </div>
              <p className="text-slate-300 text-lg max-w-md font-medium leading-relaxed pl-6 border-l-4 border-[#00f3ff]/50">
                {getC("values", "description", "home.values.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreValues.map((val, index) => (
                <div
                  key={val.id}
                  className="group relative p-12 bg-[#0a192f]/60 backdrop-blur-xl border border-[#00f3ff]/10 hover:border-[#00f3ff]/50 rounded-[3.5rem] transition-all duration-700 hover:-translate-y-4 hover:shadow-[0_0_40px_rgba(0,243,255,0.2)] overflow-hidden cursor-pointer"
                >
                  {/* Subtle Grid overlay on hover for tech feel */}
                  <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-0 group-hover:opacity-20 transition-opacity duration-700 mix-blend-overlay"></div>

                  {/* Refined Number watermark */}
                  <span className="absolute -top-6 -right-2 text-[120px] font-black text-white/[0.02] leading-none select-none group-hover:text-[#00f3ff]/5 transition-colors duration-700">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="relative z-10">
                    {/* Glowing Icon Container */}
                    <div className="w-16 h-16 bg-[#040d21] rounded-2xl flex items-center justify-center mb-10 group-hover:bg-[#00f3ff] group-hover:text-[#040d21] text-[#00f3ff] group-hover:rotate-[8deg] group-hover:scale-110 transition-all duration-500 border border-[#00f3ff]/30 group-hover:border-transparent group-hover:shadow-[0_0_20px_#00f3ff]">
                      <span className="material-symbols-outlined text-3xl">
                        {val.icon}
                      </span>
                    </div>

                    <h3 className="text-2xl font-black mb-4 text-white tracking-tight group-hover:text-[#00f3ff] transition-colors duration-500">
                      {homeContent?.values?.items?.find((item: any) => item.id === val.id)?.title || t(`home.values.items.${val.id}`).title}
                    </h3>

                    <p className="text-slate-400 leading-relaxed font-medium group-hover:text-slate-200 transition-colors duration-500">
                      {homeContent?.values?.items?.find((item: any) => item.id === val.id)?.description || t(`home.values.items.${val.id}`).description}
                    </p>
                  </div>

                  {/* Tech Accent Glow */}
                  <div className="absolute top-0 right-0 w-1 h-0 bg-[#00f3ff] group-hover:h-full transition-all duration-700 shadow-[0_0_10px_#00f3ff]"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BEGIN: Services Ecosystem */}
        <section
          className="py-32 bg-[#040d21] relative overflow-hidden section-visibility"
          id="dich-vu"
        >
          {/* 3D Ecosystem Background */}
          <Services3DCanvas />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-[#00f3ff] font-bold tracking-widest uppercase text-sm mb-4 drop-shadow-[0_0_8px_rgba(0,243,255,0.5)]">
                {getC("services", "badge", "home.services.badge")}
              </h2>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight drop-shadow-sm">
                {getC("services", "title", "home.services.title")}
              </h2>
              <p className="text-slate-300 text-xl max-w-3xl mx-auto font-medium leading-relaxed">
                {getC("services", "description", "home.services.description")}
              </p>
            </div>
            {isLoading ? (
              <div className="flex justify-center py-20">
                <div className="w-12 h-12 border-4 border-[#00f3ff]/20 border-t-[#00f3ff] rounded-full animate-spin"></div>
              </div>
            ) : (
              <ServicesTabs
                services={
                  (fetchedServices.length > 0
                    ? fetchedServices
                    : services
                  ).slice(0, 4) as any
                }
              />
            )}
          </div>
        </section>

        {/* BEGIN: Subsidiary Brands Section */}
        <BrandsSection />

        {/* BEGIN: Stats Section */}
        <section className="py-24 bg-[#040d21] relative overflow-hidden border-y border-[#00f3ff]/10 section-visibility">
          {/* Tech Decorative Background */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#0066ff]/10 rounded-full blur-[100px] -z-10"></div>
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#00f3ff]/5 rounded-full blur-[120px] -z-10"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {(homeContent?.stats || stats).map((stat: any, i: number) => {
                const numValue = parseInt(stat.value);
                const suffix = stat.value.replace(/[0-9]/g, "");

                return (
                  <div
                    key={i}
                    className="group relative p-8 rounded-3xl bg-[#0a192f]/60 backdrop-blur-xl border border-[#00f3ff]/10 hover:border-[#00f3ff]/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(0,243,255,0.2)] overflow-hidden cursor-pointer"
                  >
                    {/* Hover Accent Line */}
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#00f3ff] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top shadow-[0_0_10px_#00f3ff]"></div>

                    <div className="text-4xl md:text-5xl font-black text-white mb-3 flex items-baseline gap-1 drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]">
                      <Counter end={numValue} suffix={suffix} />
                    </div>
                    <div className="text-[#00f3ff] text-xs font-bold uppercase tracking-[0.2em] leading-relaxed drop-shadow-[0_0_5px_rgba(0,243,255,0.3)]">
                      {stat.label || t(`home.ecosystem.items.${stat.id}`)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* BEGIN: Leadership Team */}
        <section className="py-32 bg-[#040d21]/95 section-visibility" id="lanh-dao">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-24">
              <h2 className="text-[#00f3ff] font-bold tracking-widest uppercase text-sm mb-4 drop-shadow-[0_0_5px_rgba(0,243,255,0.5)]">
                {getC("leadership", "badge", "home.leadership.badge")}
              </h2>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
                {getC("leadership", "title", "home.leadership.title")}
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto font-medium leading-relaxed">
                {getC("leadership", "description", "home.leadership.description")}
              </p>
            </div>
            <LeadershipCarousel leaders={homeContent?.leadership?.items || leadership} />
          </div>
        </section>

        {/* BEGIN: Company Milestones Section */}
        <section
          className="py-24 bg-gradient-to-b from-[#040d21] via-[#0a192f] to-[#040d21] relative overflow-hidden section-visibility"
          id="cot-moc"
        >
          {/* 3D Canvas Background */}
          <Milestones3DCanvas />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-[#00f3ff] font-bold tracking-widest uppercase text-xs mb-4 drop-shadow-[0_0_5px_rgba(0,243,255,0.5)]">
                {getC("milestones", "badge", "home.milestones.journeyTitle")}
              </h2>
              <div className="h-1 w-16 bg-[#00f3ff] rounded-full mx-auto mb-6 shadow-[0_0_10px_#00f3ff]"></div>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-6">
                {getC("milestones", "title", "home.milestones.title")}
              </h2>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto font-medium leading-relaxed">
                {getC("milestones", "description", "home.milestones.description")}
              </p>
            </div>

            <div className="relative">
              {/* Simplified Timeline Line */}
              <div className="hidden lg:block absolute top-[2.5rem] left-0 w-full h-[2px] bg-[#00f3ff]/20"></div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {(homeContent?.milestones?.items || milestones).map((milestone: any, index: number) => (
                  <div key={index} className="relative group">
                    {/* Simplified Timeline Dot */}
                    <div className="hidden lg:flex absolute top-[2.5rem] left-8 -mt-2 w-4 h-4 rounded-full bg-[#040d21] border-2 border-[#00f3ff]/30 group-hover:border-[#00f3ff] group-hover:shadow-[0_0_15px_#00f3ff] group-hover:scale-125 transition-all duration-500 z-20 items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#00f3ff] opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-[0_0_5px_#00f3ff]"></div>
                    </div>

                    {/* Milestone Card - Compact & Premium Light (Glassmorphism) */}
                    <div className="bg-[#040d21]/60 backdrop-blur-xl p-8 rounded-3xl border border-[#00f3ff]/20 hover:border-[#00f3ff]/50 shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_30px_rgba(0,243,255,0.2)] transition-all duration-500 hover:-translate-y-2 relative z-10 lg:mt-16">
                      <div className="text-[#00f3ff] font-black text-3xl mb-4 tracking-tighter transition-transform origin-left drop-shadow-[0_0_5px_currentColor]">
                        {milestone.year}
                      </div>
                      <div className="w-8 h-1 bg-[#00f3ff] rounded-full mb-5 group-hover:w-16 transition-all duration-700 shadow-[0_0_5px_#00f3ff]"></div>
                      <h3 className="text-lg font-black text-white leading-tight mb-3 tracking-tight group-hover:text-[#00f3ff] transition-colors">
                        {
                          milestone.title || t(`home.milestones.items.${milestone.year.split(" ")[0]}`).title
                        }
                      </h3>
                      <p className="text-slate-300 text-sm font-medium leading-relaxed">
                        {
                          milestone.description || t(`home.milestones.items.${milestone.year.split(" ")[0]}`).description
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* BEGIN: Testimonials Section */}
        <section className="py-32 bg-[#0a192f] overflow-hidden section-visibility" id="cam-nhan">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-[#00f3ff] font-bold tracking-widest uppercase text-sm mb-4 drop-shadow-[0_0_5px_rgba(0,243,255,0.5)]">
                {getC("testimonials", "badge", "home.testimonials.badge")}
              </h2>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white">
                {getC("testimonials", "title", "home.testimonials.title")}
              </h2>
            </div>
            <TestimonialCarousel testimonials={homeContent?.testimonials?.items} />
          </div>
        </section>

        {/* BEGIN: News Section */}
        <section
          className="py-32 bg-[#040d21] relative overflow-hidden section-visibility"
          id="tin-tuc"
        >
          {/* Subtle 3D Background */}
          <News3DCanvas />

          {/* Overlay gradient to ensure text readability */}
          <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#040d21]/40 via-transparent to-[#0a192f]/90 pointer-events-none" />

          {/* Soft glows behind content */}
          <div className="absolute top-20 right-10 w-[400px] h-[400px] bg-[#00f3ff]/5 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-20 left-10 w-[500px] h-[500px] bg-[#0066ff]/5 rounded-full blur-[120px] pointer-events-none" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div>
                <h2 className="text-[#00f3ff] font-bold tracking-widest uppercase text-sm mb-4 drop-shadow-[0_0_5px_rgba(0,243,255,0.4)] flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#00f3ff] animate-pulse" />
                  {getC("news", "badge", "home.news.badge")}
                </h2>
                <h2 className="text-4xl md:text-[56px] font-black text-white tracking-tight leading-tight">
                  {getC("news", "title", "home.news.title")}
                </h2>
              </div>
              <a
                className="inline-flex items-center gap-2 text-[#00f3ff] bg-[#00f3ff]/10 border border-[#00f3ff]/20 px-6 py-3 rounded-full hover:bg-[#00f3ff]/20 hover:text-white font-bold group transition-all backdrop-blur-md"
                href="/news"
              >
                {t("home.news.viewAll")}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {isLoading
                ? [1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="bg-[#0a192f]/50 backdrop-blur-md rounded-[2.5rem] p-4 h-[400px] animate-pulse border border-[#00f3ff]/10"
                    ></div>
                  ))
                : fetchedNews.slice(0, 4).map((item, index) => (
                    <div
                      key={`${item.id}-${index}`}
                      className="group bg-[#0a192f]/60 backdrop-blur-lg rounded-[2.5rem] p-4 shadow-[0_8px_30px_rgba(0,0,0,0.3)] border border-[#00f3ff]/10 hover:border-[#00f3ff]/40 hover:shadow-[0_0_40px_rgba(0,243,255,0.15)] hover:-translate-y-2 transition-all duration-500 cursor-pointer flex flex-col h-full"
                      onClick={() => router.push(`/news/${item.slug}`)}
                    >
                      <div className="relative overflow-hidden rounded-[2rem] mb-6 aspect-[4/3] flex-shrink-0 border border-white/5">
                        <img
                          alt={`Tin tức AHV Holding: ${item.title}`}
                          className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-1 transition-transform duration-700"
                          src={item.imageUrl}
                        />
                        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md border border-[#00f3ff]/30 text-[#00f3ff] text-[10px] font-black px-4 py-1.5 rounded-full tracking-wider uppercase">
                          {t(`common.categories.${item.category}`) === `common.categories.${item.category}`
                            ? item.category
                            : t(`common.categories.${item.category}`)}
                        </div>
                      </div>
                      <div className="px-2 pb-4 flex flex-col flex-grow">
                        <div className="text-[#00f3ff] font-semibold text-xs flex items-center gap-2 mb-3 opacity-90">
                          <Calendar className="w-3.5 h-3.5" />{" "}
                          {formatDate(item.date, language)}
                        </div>
                        <h3
                          className="text-lg font-bold text-white group-hover:text-[#00f3ff] transition-colors line-clamp-2 leading-snug mb-3 flex-grow"
                          dangerouslySetInnerHTML={{
                            __html:
                              t(`home.news.items.${item.slug}`).title ||
                              item.title,
                          }}
                        />
                        <p className="text-slate-400 text-sm font-medium leading-relaxed line-clamp-3 mb-6">
                          {formatExcerpt(
                            t(`home.news.items.${item.slug}`).excerpt ||
                              item.excerpt ||
                              item.content,
                            120,
                          )}
                        </p>
                        <a
                          className="inline-flex items-center gap-1.5 text-[#00f3ff] font-bold text-sm group/btn mt-auto"
                          href={`/news/${item.slug}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            router.push(`/news/${item.slug}`);
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-[#00f3ff]/10 flex items-center justify-center group-hover/btn:bg-[#00f3ff] group-hover/btn:text-black transition-colors">
                            <ArrowRight className="w-4 h-4" />
                          </div>
                          <span className="group-hover/btn:underline decoration-2 underline-offset-4">
                            {t("home.news.readMore")}
                          </span>
                        </a>
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </section>

        {/* BEGIN: Careers Section */}
        <section className="py-32 bg-[#0a192f] overflow-hidden section-visibility" id="tuyen-dung">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div>
                <h2 className="text-[#00f3ff] font-bold tracking-widest uppercase text-sm mb-6 drop-shadow-[0_0_5px_rgba(0,243,255,0.5)]">
                  {getC("careers", "badge", "home.careers.badge")}
                </h2>
                <div className="h-1.5 w-24 bg-[#00f3ff] rounded-full mb-6 shadow-[0_0_10px_#00f3ff]"></div>
                <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-8 leading-[1.1]">
                  {getC("careers", "title", "home.careers.title").split(" AHV")[0]} <br />
                  <span className="text-[#00f3ff] drop-shadow-[0_0_8px_rgba(0,243,255,0.3)]">
                    AHV Holding
                  </span>
                </h2>
                <p className="text-lg text-slate-300 mb-10 leading-relaxed font-medium">
                  {getC("careers", "description", "home.careers.description")}
                </p>
                <div className="grid sm:grid-cols-2 gap-6 mb-12">
                  {careerCoreValues.slice(0, 4).map((val, index) => {
                    const Icon = iconMap[val.icon] || Users;
                    return (
                      <div
                        key={`${val.id}-${index}`}
                        className="bg-[#040d21]/60 backdrop-blur-xl p-8 rounded-[2rem] border border-[#00f3ff]/20 hover:border-[#00f3ff]/50 transition-all hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(0,243,255,0.1)]"
                      >
                        <div className="w-12 h-12 bg-[#00f3ff]/10 rounded-xl flex items-center justify-center mb-6 shadow-sm">
                          <Icon className="text-[#00f3ff] w-6 h-6 drop-shadow-[0_0_3px_currentColor]" />
                        </div>
                        <h4 className="font-extrabold text-white mb-2">
                          {t(`home.careers.values.${val.id}`).title}
                        </h4>
                        <p className="text-slate-400 text-sm font-medium">
                          {t(`home.careers.values.${val.id}`).description}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <a
                  className="text-[#00f3ff] border border-[#00f3ff] bg-[#00f3ff]/5 px-10 py-5 rounded-2xl font-extrabold hover:bg-[#00f3ff]/20 transition-all hover:shadow-[0_0_20px_rgba(0,243,255,0.4)] inline-block cursor-pointer backdrop-blur-md hover:-translate-y-1"
                  href="/careers"
                >
                  Xem tất cả vị trí
                </a> 
              </div>
              <div className="space-y-4">
                {isLoading
                  ? [1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-28 bg-[#040d21]/80 rounded-[2.5rem] animate-pulse border border-[#00f3ff]/10"
                      ></div>
                    ))
                  : fetchedJobs
                      .slice(0, 4)
                      .map((job, index) => (
                        <div
                          key={`${job.id}-${index}`}
                          className="group bg-[#040d21]/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-[#00f3ff]/20 hover:border-[#00f3ff]/70 hover:bg-[#00f3ff]/5 hover:shadow-[0_0_30px_rgba(0,243,255,0.15)] transition-all flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 cursor-pointer"
                          onClick={() => router.push(`/careers/${job.slug}`)}
                        >
                          <div>
                            <h4 className="font-extrabold text-xl text-white group-hover:text-[#00f3ff] transition-colors drop-shadow-[0_0_2px_currentColor]">
                              {job.title}
                            </h4>
                            <div className="flex flex-wrap items-center gap-4 mt-3">
                              <span className="flex items-center gap-1.5 text-[10px] font-bold text-[#00f3ff] bg-[#00f3ff]/10 px-3 py-1.5 rounded-full border border-[#00f3ff]/20">
                                <MapPin className="w-3 h-3" /> {t(`home.careers.jobInfo.${job.location.replace(/\./g, "")}`) !== `home.careers.jobInfo.${job.location.replace(/\./g, "")}` 
                            ? t(`home.careers.jobInfo.${job.location.replace(/\./g, "")}`) 
                            : job.location}
                        </span>
                        <span className="flex items-center gap-1.5 text-[10px] font-bold text-[#00f3ff] bg-[#00f3ff]/10 px-3 py-1.5 rounded-full border border-[#00f3ff]/20">
                          <Clock className="w-3 h-3" /> {t(`home.careers.jobInfo.${job.type}`) !== `home.careers.jobInfo.${job.type}`
                            ? t(`home.careers.jobInfo.${job.type}`)
                            : job.type}
                        </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 relative z-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyJobLink(job.slug);
                              }}
                              className="w-10 h-10 rounded-xl bg-[#00f3ff]/10 border border-[#00f3ff]/20 flex items-center justify-center text-[#00f3ff] hover:bg-[#00f3ff] hover:text-[#040d21] transition-all cursor-pointer group/copy"
                              title={t("careersPage.detail.copyLink") || "Sao chép liên kết"}
                            >
                              <Link2 className="w-5 h-5 transition-transform group-hover/copy:scale-110" />
                            </button>
                            <div className="w-10 h-10 rounded-xl bg-[#00f3ff]/10 text-[#00f3ff] border border-[#00f3ff]/30 group-hover:bg-[#00f3ff] group-hover:text-[#040d21] group-hover:shadow-[0_0_15px_#00f3ff] flex items-center justify-center transition-all duration-300">
                              <Plus className="w-6 h-6" />
                            </div>
                          </div>
                        </div>
                      ))}
              </div>
            </div>
          </div>
        </section>

        <MapSection />
      </main>
      <Footer />
    </>
  );
}
